// App.js
import { useEffect, useState, useCallback } from "react";
import { Link, animateScroll as scroll } from "react-scroll";
import { motion } from "framer-motion";
import { ExternalLink, FileText, Quote, Mail, Linkedin, Play } from "lucide-react";

const quotes = [
  {
    text: "Strong grasp of AI technologies and user experience—delivering an impactful conversational AI pilot that improved efficiency. A great collaborator who overdelivered on expectations.",
    name: "Liesbeth Goossen",
    role: "Senior Manager, HP Inc.",
  },
  {
    text: "Sem consistently demonstrated excellent collaboration skills, proactivity, analytical thinking, and strong problem-solving abilities. His ability to combine strategic thinking with hands-on execution made him a highly reliable and impactful team member. I would not hesitate to hire Sem again.",
    name: "Sandra Gonzalez",
    role: "Direct Manager · Manager of Contact Center Strategy & Innovation, HP Inc.",
  },
  {
    text: "Sem combines technical excellence with empathy and vision—a rare blend that makes him an exceptional emerging leader in AI innovation.",
    name: "Frederike Hofstra",
    role: "Senior Manager, HP Inc.",
  },
  {
    text: "Sem stands out for his initiative and ability to learn quickly and adapt—approaching every challenge with curiosity and rigor to deliver data-driven insights.",
    name: "Debbie Marusic",
    role: "Program Manager, HP Inc.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function App() {
  const [q, setQ] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((idx) => {
    setQ(idx);
    setPaused(true);
    setTimeout(() => setPaused(false), 8000);
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setQ((i) => (i + 1) % quotes.length);
    }, 9500);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div className="bg-neutral-950 text-white min-h-screen selection:bg-blue-600 selection:text-white">
      {/* NAV */}
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-neutral-950/70 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => scroll.scrollToTop({ duration: 500 })}
            className="font-semibold tracking-tight hover:opacity-80"
          >
            Sem Ferid
          </button>
          <div className="hidden md:flex items-center gap-6 text-sm text-gray-300">
            <Link to="hero" smooth duration={500} className="cursor-pointer hover:text-white">
              Home
            </Link>
            <Link to="letters" smooth duration={500} className="cursor-pointer hover:text-white">
              Letters
            </Link>
            <Link to="experience" smooth duration={500} className="cursor-pointer hover:text-white">
              Experience
            </Link>
            <Link to="projects" smooth duration={500} className="cursor-pointer hover:text-white">
              Projects
            </Link>
            <Link to="contact" smooth duration={500} className="cursor-pointer hover:text-white">
              Contact
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/Resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5"
            >
              Résumé
            </a>
            <a
              href="/LoRCompilation.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700"
            >
              Letters
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section id="hero" className="pt-24 md:pt-28">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid md:grid-cols-[1fr_300px] gap-10 items-center mb-12">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
              <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
                Product leader who builds for <span className="text-blue-500">impact</span>, not just features
              </h1>
              <p className="mt-4 text-gray-400 max-w-2xl text-lg">
                I bridge technical depth with user-centric thinking to ship products that solve real problems.
                · Penn Engineering (CS + Entrepreneurship) · Seeking APM roles.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="/Resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl px-5 py-3 bg-white text-black hover:opacity-90"
                >
                  <FileText size={18} /> View Résumé
                </a>
                <a
                  href="/LoRCompilation.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl px-5 py-3 border border-white/10 hover:bg-white/5"
                >
                  <Quote size={18} /> Read Letters of Recommendation
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1, transition: { duration: 0.6 } }}
              viewport={{ once: true }}
              className="flex justify-center md:justify-end"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-blue-500/30 shadow-2xl shadow-blue-500/10 w-[280px] h-[280px]">
                <img
                  src="/headshot.png"
                  alt="Sem Ferid"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>

          {/* PM Attributes Grid */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="grid md:grid-cols-4 gap-4 mt-8"
          >
            <div className="rounded-xl border border-white/10 p-5 bg-gradient-to-b from-neutral-900 to-neutral-950 hover:border-blue-500/30 transition-colors">
              <div className="text-2xl mb-2">🎯</div>
              <h3 className="text-sm font-semibold text-blue-400 mb-1">Cross-Functional Leadership</h3>
              <p className="text-xs text-gray-400">Aligned 5+ teams across engineering, operations & business at HP</p>
            </div>
            <div className="rounded-xl border border-white/10 p-5 bg-gradient-to-b from-neutral-900 to-neutral-950 hover:border-blue-500/30 transition-colors">
              <div className="text-2xl mb-2">📊</div>
              <h3 className="text-sm font-semibold text-blue-400 mb-1">Data-Driven Decisions</h3>
              <p className="text-xs text-gray-400">Tracked AHT, deflection & CSAT to drive 22% cost reduction</p>
            </div>
            <div className="rounded-xl border border-white/10 p-5 bg-gradient-to-b from-neutral-900 to-neutral-950 hover:border-blue-500/30 transition-colors">
              <div className="text-2xl mb-2">👥</div>
              <h3 className="text-sm font-semibold text-blue-400 mb-1">User-Centric Thinking</h3>
              <p className="text-xs text-gray-400">Designed Customer Journey Analytics to uncover pain points</p>
            </div>
            <div className="rounded-xl border border-white/10 p-5 bg-gradient-to-b from-neutral-900 to-neutral-950 hover:border-blue-500/30 transition-colors">
              <div className="text-2xl mb-2">⚙️</div>
              <h3 className="text-sm font-semibold text-blue-400 mb-1">Technical Depth</h3>
              <p className="text-xs text-gray-400">CS degree + hands-on AI/ML engineering enables deep technical partnership</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-18 md:py-20 bg-gradient-to-b from-neutral-950 to-neutral-900">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4">My Product Philosophy</h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              "Build for <span className="text-blue-400 font-semibold">impact, not features</span>. The best products solve real problems
              with measurable outcomes—not just showcase what's technically possible. I focus on what moves the needle
              for users and the business, shipping iteratively and validating ruthlessly."
            </p>
          </motion.div>
        </div>
      </section>

      {/* LETTERS SPOTLIGHT */}
      <section id="letters" className="py-18 md:py-24">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl font-semibold mb-8"
          >
            Professional Recommendations
          </motion.h2>

          {/* Rotating quote */}
          <motion.div
            key={q}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mx-auto max-w-3xl"
          >
            <div className="rounded-2xl border border-white/10 bg-neutral-900 p-7">
              <p className="text-lg md:text-xl text-gray-200 italic leading-relaxed">"{quotes[q].text}"</p>
              <p className="mt-4 text-gray-400">
                — <span className="text-white font-medium">{quotes[q].name}</span>, {quotes[q].role}
              </p>
            </div>
          </motion.div>

          {/* Dot navigation */}
          <div className="flex justify-center gap-2 mt-5">
            {quotes.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === q ? "bg-blue-500 w-5" : "bg-white/20 hover:bg-white/40"
                }`}
                aria-label={`Go to recommendation ${idx + 1}`}
              />
            ))}
          </div>

          {/* CTA to full PDF */}
          <div className="mt-8">
            <a
              href="/LoRCompilation.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-xl font-medium"
            >
              <ExternalLink size={18} /> View Full Letters (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" className="py-18 md:py-24 bg-neutral-950">
        <div className="mx-auto max-w-6xl px-4">
          <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-3xl font-semibold text-center mb-10">
            Experience
          </motion.h2>

          <div className="grid gap-6">
            <Job
              company="HP Inc."
              role="Project Management Intern — Digital Contact Center Transformation"
              period="Aug 2024 – Sept 2025"
              bullets={[
                "Managed roadmap & Agile delivery for two multimodal AI bots automating HP's support system.",
                "Coordinated cross-functional delivery across 5+ projects, aligning engineering, operations, and business leads.",
                "Tracked AHT, deflection, and CSAT to drive a 22% cost-per-case reduction in North America.",
                "Reported insights to leadership, informing automation rollout across multiple product lines.",
              ]}
            />
            <Job
              company="HP Inc."
              role="AI / Machine Learning Intern"
              period="May 2024 – Aug 2024"
              bullets={[
                "Defined vision for an AI-driven customer experience, enabling >90% automation via data insights & competitive analysis.",
                "Designed Customer Journey Analytics prototype to uncover weak points and size opportunities.",
                "Contributed to a 20% reduction in Average Handle Time (AHT), improving chat efficiency.",
              ]}
            />
            <Job
              company="Neutrino AI"
              role="Software Engineering Intern"
              period="Jan 2024 – Oct 2024"
              bullets={[
                "Integrated multiple LLM providers through a model router API for faster, more efficient routing.",
                "Built analytics to visualize user growth and model usage trends to guide strategy.",
                "Reduced model latency and improved routing efficiency by 95% through algorithmic optimization.",
              ]}
            />
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-18 md:py-24 bg-gradient-to-b from-neutral-950 to-neutral-900">
        <div className="mx-auto max-w-6xl px-4">
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl font-semibold text-center mb-10"
          >
            Projects & Ventures
          </motion.h2>

          {/* StrikeZone — featured */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-2xl border border-white/10 bg-neutral-900 p-6 md:p-8 mb-6"
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs px-2.5 py-1 rounded-full bg-blue-600/20 text-blue-400 border border-blue-500/20 font-medium">
                Founder
              </span>
              <span className="text-xs text-gray-500">Aug 2025 – Dec 2025</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-1">StrikeZone</h3>
            <p className="text-sm text-blue-400 mb-5">Smart Combat Sports Training & Analytics Startup</p>

            <div className="grid md:grid-cols-2 gap-6 items-start">
              {/* Pitch video embed */}
              <div className="rounded-xl overflow-hidden bg-neutral-800 border border-white/10" style={{ aspectRatio: "16/9" }}>
                <iframe
                  src="https://drive.google.com/file/d/1Z0HBSOezUfy0GMYU4oC6UhCiYW8sWzWa/preview"
                  title="StrikeZone Investor Pitch"
                  className="w-full h-full"
                  allow="autoplay"
                  allowFullScreen
                  style={{ border: "none", display: "block" }}
                />
              </div>

              {/* Description + bullets */}
              <div>
                <p className="text-gray-300 text-sm leading-relaxed mb-5">
                  AI-driven smart combat gear that classifies strikes and detects performance errors in real time.
                  Built end-to-end from user research through investor pitch.
                </p>
                <ul className="space-y-3 text-sm text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                    Conducted 30+ user interviews to define product requirements and validate pain points
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                    Synthesized insights into product roadmap & competitive positioning vs. FightCamp and Hykso
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                    Pitched to a panel of investors, securing strategic feedback on GTM strategy and hardware feasibility
                  </li>
                </ul>
                <div className="mt-6">
                  <a
                    href="https://drive.google.com/file/d/1Z0HBSOezUfy0GMYU4oC6UhCiYW8sWzWa/view"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors"
                  >
                    <Play size={14} /> Watch Pitch
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* PickUp */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-2xl border border-white/10 bg-neutral-900 p-6 md:p-8"
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs px-2.5 py-1 rounded-full bg-neutral-700/60 text-gray-300 border border-white/10 font-medium">
                Personal Project
              </span>
              <span className="text-xs text-gray-500">March 2024 – May 2024</span>
            </div>
            <h3 className="text-xl font-semibold text-white mb-1">PickUp</h3>
            <p className="text-sm text-blue-400 mb-4">TypeScript · React Native · Supabase · Expo</p>
            <p className="text-gray-300 text-sm leading-relaxed mb-5">
              Mobile app connecting basketball players through real-time court activity data and session scheduling.
            </p>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                30+ gym surveys to form user requirements and validate core product features
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                Defined features including player authentication, live court heat maps, and game session coordination
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                Engineered a dynamic map interface using React Native Maps and Google Maps API
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-18 md:py-24">
        <div className="mx-auto max-w-6xl px-4">
          <div className="rounded-2xl border border-white/10 p-8 bg-gradient-to-b from-neutral-900 to-neutral-950 text-center">
            <h3 className="text-2xl font-semibold">Let's build something great.</h3>
            <p className="mt-2 text-gray-400">Open to APM and SWE new-grad roles. Based in Philadelphia; flexible on location.</p>
            <div className="mt-5 flex flex-wrap gap-3 justify-center">
              <a href="mailto:semf@seas.upenn.edu" className="inline-flex items-center gap-2 rounded-xl px-5 py-3 bg-white text-black hover:opacity-90">
                <Mail size={18} /> Email
              </a>
              <a
                href="https://linkedin.com/in/semf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 border border-white/10 hover:bg-white/5"
              >
                <Linkedin size={18} /> LinkedIn
              </a>
              <a
                href="/Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 border border-white/10 hover:bg-white/5"
              >
                <FileText size={18} /> Résumé PDF
              </a>
              <a
                href="/LoRCompilation.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 bg-blue-600 hover:bg-blue-700"
              >
                <ExternalLink size={18} /> Letters PDF
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-gray-500 border-t border-white/10">
        © {new Date().getFullYear()} Sem Ferid
      </footer>
    </div>
  );
}

function Job({ company, role, period, bullets }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      variants={fadeUp}
      className="rounded-2xl border border-white/10 p-6 bg-neutral-900"
    >
      <h3 className="text-lg font-semibold text-blue-400">{company}</h3>
      <p className="text-sm text-gray-300 mt-1">{role}</p>
      <p className="text-xs text-gray-500 mb-4">{period}</p>
      <ul className="space-y-2 text-gray-300 text-sm">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            • <span>{b}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
